import { useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiDelete, apiGet, apiPost } from 'api'
import AuthContext from 'context/auth-context'

export const ASK_CONVERSATIONS_KEY = ['ask/conversations']
export const askConversationKey = (id) => [`ask/conversations/${id}`]

// Submit a question (or the answer to the assistant's question). Without a conversationId the
// server starts a new conversation. Returns the conversation; the turn runs in the background.
export function useSendAskMessage({ options = {} } = {}) {
  const token = useContext(AuthContext)

  return useMutation(
    ({ message, conversationId }) =>
      apiPost({
        url: 'ask',
        token,
        objectName: 'ask',
        params: { message, conversation_id: conversationId ?? undefined }
      }),
    { ...options }
  )
}

// Stitch a delta onto what is cached, by id, so two polls sharing a cursor never double-render.
const mergeById = (cached = [], incoming = []) => {
  const merged = [...cached]
  const index = new Map(merged.map((item, i) => [item.id, i]))
  incoming.forEach((item) => {
    if (index.has(item.id)) {
      merged[index.get(item.id)] = item
    } else {
      index.set(item.id, merged.length)
      merged.push(item)
    }
  })
  return merged
}

// Poll a conversation. Incremental, like the report assistant: it sends the last event it holds and
// receives only newer events, plus the charts those events draw.
export function useAskConversation({ conversationId, options = {} }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const key = askConversationKey(conversationId)

  return useQuery(
    key,
    async () => {
      const cached = queryClient.getQueryData(key)
      const cachedEvents = cached?.events ?? []
      const data = await apiGet({
        url: `ask/conversations/${conversationId}`,
        token,
        params: { after_event_id: cachedEvents[cachedEvents.length - 1]?.id }
      })
      return {
        ...data,
        events: mergeById(cachedEvents, data.events ?? []),
        charts: mergeById(cached?.charts ?? [], data.charts ?? [])
      }
    },
    { enabled: !!conversationId, ...options }
  )
}

export function useAskConversations({ options = {} } = {}) {
  const token = useContext(AuthContext)

  return useQuery(ASK_CONVERSATIONS_KEY, () => apiGet({ url: 'ask/conversations', token }), { ...options })
}

export function useCancelAskTurn({ options = {} } = {}) {
  const token = useContext(AuthContext)

  return useMutation((conversationId) => apiPost({ url: `ask/conversations/${conversationId}/cancel`, token }), {
    ...options
  })
}

export function useDeleteAskConversation({ options = {} } = {}) {
  const token = useContext(AuthContext)

  return useMutation((conversationId) => apiDelete({ url: `ask/conversations/${conversationId}`, token }), {
    ...options
  })
}
